import React, { useEffect, useMemo, useState } from "react";
import { useHistory, Redirect } from "react-router-dom";
import {
  Button,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import { Add, Search, Group } from "@material-ui/icons";
import { DataGrid } from "@mui/x-data-grid";
import firebase from "../firebase";
import { dataGridSx, palette } from "../theme";
import PageContainer from "../Components/UI/PageContainer";
import PageHeader from "../Components/UI/PageHeader";

export default function SessionsPage() {
  const history = useHistory();
  const signedIn = global.config.uid !== "";
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [kids, setKids] = useState([]);
  const [search, setSearch] = useState("");
  const [kidFilter, setKidFilter] = useState("all");

  useEffect(() => {
    if (!signedIn) return;
    const db = firebase.firestore();
    const base = db.collection("Main").doc(global.config.uid);

    const unsubSessions = base
      .collection("Sessions")
      .orderBy("Date", "desc")
      .onSnapshot((snapshot) => {
        setSessions(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            fullName: doc.data().fullName,
            kid: doc.data().Kid,
            location: doc.data().Location,
            notes: doc.data().Notes,
            sessionType: doc.data().sessionType,
            date: doc.data().Date ? doc.data().Date.toDate() : null,
          }))
        );
        setLoading(false);
      });

    const unsubKids = base
      .collection("Kids")
      .orderBy("LastName")
      .onSnapshot((snapshot) => {
        setKids(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            fullName: doc.data().FirstName + " " + doc.data().LastName,
          }))
        );
      });

    return () => {
      unsubSessions();
      unsubKids();
    };
  }, [signedIn]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sessions.filter((s) => {
      if (kidFilter !== "all" && s.kid !== kidFilter) return false;
      if (!q) return true;
      return (
        (s.fullName || "").toLowerCase().includes(q) ||
        (s.notes || "").toLowerCase().includes(q) ||
        (s.location || "").toLowerCase().includes(q)
      );
    });
  }, [sessions, search, kidFilter]);

  const columns = [
    {
      field: "date",
      headerName: "Date",
      width: 130,
      valueFormatter: (params) =>
        params.value
          ? params.value.toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "",
    },
    {
      field: "fullName",
      headerName: "Kid",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => <span style={{ fontWeight: 700 }}>{params.value}</span>,
    },
    {
      field: "location",
      headerName: "Location",
      width: 170,
      renderCell: (params) =>
        params.value ? (
          <Chip
            size="small"
            label={params.value}
            sx={{ backgroundColor: palette.primarySoft, color: palette.primaryDark }}
          />
        ) : null,
    },
    {
      field: "sessionType",
      headerName: "Type",
      width: 120,
      renderCell: (params) =>
        params.value ? (
          <Chip
            size="small"
            label={params.value === "GroupSession" ? "Group" : "Individual"}
            sx={{
              backgroundColor: params.value === "GroupSession" ? "#FEF3C7" : "#DCFCE7",
              color: params.value === "GroupSession" ? "#92400E" : "#166534",
            }}
          />
        ) : null,
    },
    { field: "notes", headerName: "Notes", flex: 2, minWidth: 240 },
  ];

  if (!signedIn) return <Redirect to="/" />;

  return (
    <PageContainer>
      <PageHeader
        title="Sessions"
        subtitle="Every session and its notes, newest first"
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<Group />}
              onClick={() => history.push("/group-profile")}
            >
              Add Group Session
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={() => history.push("/sessions-profile")}
            >
              Add Session
            </Button>
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={7} md={8}>
          <TextField
            fullWidth
            placeholder="Search notes, kids or locations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: "#94A3B8" }} />
                </InputAdornment>
              ),
            }}
            inputProps={{ "aria-label": "Search sessions" }}
          />
        </Grid>
        <Grid item xs={12} sm={5} md={4}>
          <TextField
            select
            fullWidth
            label="Kid"
            value={kidFilter}
            onChange={(e) => setKidFilter(e.target.value)}
          >
            <MenuItem value="all">All kids</MenuItem>
            {kids.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.fullName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <DataGrid
        autoHeight
        loading={loading}
        rows={rows}
        columns={columns}
        getRowHeight={() => "auto"}
        onRowClick={(params) => history.push("/sessions-profile?sessionID=" + params.id)}
        disableRowSelectionOnClick
        initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
        pageSizeOptions={[15, 30, 50]}
        localeText={{ noRowsLabel: "No sessions yet — log your first session" }}
        sx={{
          ...dataGridSx,
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #EEF2F6",
            paddingTop: "10px",
            paddingBottom: "10px",
          },
        }}
      />
    </PageContainer>
  );
}
